import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import accountingClientsAPI from "../../api/accountingClientsAPI";
import { INDUSTRY_TEMPLATES } from "../../constants/industryTemplates";
import { TAX_ACCOUNT_PRESETS } from "../../constants/taxAccountPresets";
import useRecordLock from "../../hooks/useRecordLock";
import RecordLockBanner from "../../components/RecordLockBanner";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import saveAccountingClient from "./saveAccountingClient";
import WhtProfileFields from "./WhtProfileFields";
import BranchCodeField, { isValidBranchCode, BRANCH_CODE_ERROR } from "./BranchCodeField";
import { useAuth } from "../../context/AuthContext";


const STEPS = [
  { key: "basic", label: "Basic Info" },
  { key: "type", label: "Client Type Details" },
  { key: "contact", label: "Contact & Address" },
  { key: "tax", label: "Tax Obligations" },
];

const NewAccountingClientPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isLocked, isOwner, lockedByName, lockedAt, releaseLock } = useRecordLock(id, "accountingClient");
  // Branch code + withholding tax profile are accounting-firm fields; other
  // tenant types (billing, mrwd) keep the form — and the payload — as before.
  const { user } = useAuth();
  const isAccountingFirm = user?.tenantType === "accounting_firm";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const [formData, setFormData] = useState({
    industryTemplate: "general_service", 
    clientType: "individual",
    name: "",
    tin: "",
    rdoCode: "",
    branchCode: "0000",
    // Only fields the user sets are sent; the rest take schema defaults and
    // hasEmployees falls back to the compensation obligation (getWhtProfile).
    whtProfile: {},
    contactInfo: { email: "", phone: "", mobile: "" },
    address: {
      unitFloor: "",
      building: "",
      street: "",
      barangay: "",
      cityMunicipality: "",
      province: "",
      zipCode: "",
    },
    taxObligations: {
      percentageTax: false,
      vat: false,
      withholdingTaxExpanded: false,
      withholdingTaxCompensation: false,
      exciseTax: false,
      incomeTax: false,
      withholdingFinal: false,
    },
    corporationDetails: {
      secRegistrationNumber: "",
      businessStyle: "",
      dateOfIncorporation: "",
    },
    individualDetails: {
      birthDate: "",
      birthPlace: "",
      citizenship: "",
      occupation: "",
    },
  });

  useEffect(() => {
    if (id) {
      setIsEdit(true);
      fetchClientData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      const response = await accountingClientsAPI.getById(id);
      const client = response.data.client;

      setFormData({
        industryTemplate: client.industryTemplate || "general_service",
        clientType: client.clientType || "individual",
        name: client.name || "",
        tin: client.tin || "",
        rdoCode: client.rdoCode || "",
        branchCode: client.branchCode || "0000",
        whtProfile: client.whtProfile || {},
        contactInfo: client.contactInfo || { email: "", phone: "", mobile: "" },
        address: client.address || {
          unitFloor: "",
          building: "",
          street: "",
          barangay: "",
          cityMunicipality: "",
          province: "",
          zipCode: "",
        },
        taxObligations: client.taxObligations || {
          percentageTax: false,
          vat: false,
          withholdingTaxExpanded: false,
          withholdingTaxCompensation: false,
          exciseTax: false,
          incomeTax: false,
          withholdingFinal: false,
        },
        corporationDetails: client.corporationDetails || {
          secRegistrationNumber: "",
          businessStyle: "",
          dateOfIncorporation: "",
        },
        individualDetails: client.individualDetails || {
          birthDate: "",
          birthPlace: "",
          citizenship: "",
          occupation: "",
        },
      });
    } catch (err) {
      setError(err.message || "Failed to load client data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (path, value) => {
    const keys = path.split(".");
    setFormData((prev) => {
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleTaxObligationChange = (obligation, checked) => {
    setFormData((prev) => ({
      ...prev,
      taxObligations: { ...prev.taxObligations, [obligation]: checked },
    }));
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.name.trim()) errors.push("Client name is required");
    if (!formData.tin.trim()) errors.push("TIN is required");
    if (!formData.rdoCode.trim()) errors.push("RDO Code is required");
    if (isAccountingFirm && !isValidBranchCode(formData.branchCode)) errors.push(BRANCH_CODE_ERROR);

    if (formData.clientType === "corporation") {
      if (!formData.corporationDetails.secRegistrationNumber?.trim()) {
        errors.push("SEC Registration Number is required for corporations");
      }
    }

    return errors;
  };

  const validateStep = () => {
    const stepKey = STEPS[stepIndex].key;
    const errors = [];

    if (stepKey === "basic") {
      if (!formData.name.trim()) errors.push("Client name is required");
      if (!formData.clientType) errors.push("Client type is required");
      if (!formData.tin.trim()) errors.push("TIN is required");
      if (!formData.rdoCode.trim()) errors.push("RDO Code is required");
      if (isAccountingFirm && !isValidBranchCode(formData.branchCode)) errors.push(BRANCH_CODE_ERROR);
    }

    if (stepKey === "type") {
      if (formData.clientType === "corporation") {
        if (!formData.corporationDetails.secRegistrationNumber?.trim()) {
          errors.push("SEC Registration Number is required for corporations");
        }
      }
      // Individual details are optional (keep flexible)
    }

    // contact + tax are optional steps; don’t hard block
    return errors;
  };

  const handleNext = () => {
    const errors = validateStep();
    if (errors.length) {
      setError(errors.join(", "));
      return;
    }
    setError(null);
    setStepIndex((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setError(null);
    setStepIndex((s) => Math.max(s - 1, 0));
  };

// Only the API calls are guarded — see saveAccountingClient.js for the
// Idempotency-Key lifecycle.
const saveClient = useCallback(
  (payload) => saveAccountingClient({ isEdit, id, payload }),
  [isEdit, id]
);

const { mutate: runSave, isPending: saving } = useGuardedMutation(saveClient);

const handleSubmit = async () => {
  const validationErrors = validateForm();
  if (validationErrors.length > 0) {
    setError(validationErrors.join(", "));
    return;
  }

  setError("");
  setSuccess("");

  const { branchCode, whtProfile, ...rest } = formData;
  const payload = {
    ...rest,
    ...(isAccountingFirm ? { branchCode: branchCode.trim(), whtProfile } : {}),
    contactInfo: Object.fromEntries(
      Object.entries(formData.contactInfo).filter(([_, v]) => v !== "")
    ),
    address: Object.fromEntries(
      Object.entries(formData.address).filter(([_, v]) => v !== "")
    ),
  };

  try {
    const message = await runSave(payload);
    if (message === undefined) return; // a save is already in flight
    setSuccess(message);

    if (isOwner) {
      await releaseLock();
    }

    setTimeout(() => navigate("/client/accounting-clients"), 1400);
  } catch (err) {
    console.error(err);
    // accountingClientsAPI rethrows the server's message as a plain string.
    setError(typeof err === "string" ? err : err?.response?.data?.message || err?.message || "Something went wrong");
  }
};

const handleCancel = async () => {
  if (isOwner) {
    await releaseLock();
  }
  navigate("/client/accounting-clients");
};


  const taxObligations = useMemo(
    () => [
      {
        key: "percentageTax",
        title: "Percentage Tax",
        subtitle: "BIR Form 2551Q",
        desc: "Quarterly percentage tax filings",
      },
      {
        key: "vat",
        title: "VAT",
        subtitle: "BIR Form 2550Q",
        desc: "Quarterly VAT on sales & purchases",
      },
      {
        key: "withholdingTaxExpanded",
        title: "Withholding Tax (Expanded)",
        subtitle: "BIR Form 2307",
        desc: "Expanded withholding tax certificates",
      },
      {
        key: "withholdingTaxCompensation",
        title: "Withholding Tax (Compensation)",
        subtitle: "BIR Form 2316",
        desc: "Employee compensation withholding",
      },
      {
        key: "withholdingFinal",
        title: "Withholding Tax (Final)",
        subtitle: "BIR Form 2306",
        desc: "Final withholding certificates",
      },
      {
        key: "incomeTax",
        title: "Income Tax",
        subtitle: "BIR Form 1701 / 1702",
        desc: "Annual income tax filings",
      },
      {
        key: "exciseTax",
        title: "Excise Tax",
        subtitle: "If Applicable",
        desc: "Excise tax for specific industries",
      },
    ],
    []
  );

  const completionBadge = (idx) => {
    if (idx < stepIndex) return "bg-teal-600 text-white border-teal-600";
    if (idx === stepIndex) return "bg-white text-teal-700 border-teal-600";
    return "bg-white text-gray-400 border-gray-200";
  };

  return (
    <Layout pageTitle={isEdit ? "Edit Accounting Client" : "New Accounting Client"}>
      <div className="max-w-5xl mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">{isEdit ? "Edit Accounting Client" : "New Accounting Client"}</h1>
            <p className="text-sm text-gray-600 mt-1">
              Add a client, then your COA and tax modules follow the selected context.
            </p>
          </div>

          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Back to List
          </button>
        </div>

        <RecordLockBanner isLocked={isLocked} lockedByName={lockedByName} lockedAt={lockedAt} />

        {error && (
          <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-300 text-green-700 px-4 py-3 rounded mb-4">{success}</div>
        )}

        {/* Stepper */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-wrap gap-3">
            {STEPS.map((s, idx) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setStepIndex(idx)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${completionBadge(idx)}`}
                disabled={loading}
              >
                <span className="w-6 h-6 rounded-full border flex items-center justify-center text-xs">
                  {idx + 1}
                </span>
                <span className="font-medium">{s.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <fieldset disabled={isLocked || saving}>
        <div className="bg-white rounded-lg shadow-md border p-6">
          {STEPS[stepIndex].key === "basic" && (
            <section>
              <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name *">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Client name"
                    disabled={loading || isLocked}
                  />
                </Field>

                <Field label="Client Type *">
                  <select
                    value={formData.clientType}
                    onChange={(e) => handleInputChange("clientType", e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    disabled={loading || isLocked}
                  >
                    <option value="individual">Individual</option>
                    <option value="corporation">Corporation</option>
                    <option value="opc">OPC</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </Field>

               <Field label="Industry Template *">
                <select
                  value={formData.industryTemplate || "general_service"}
                  onChange={(e) => handleInputChange("industryTemplate", e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={loading || isLocked}
                >
                  <option value="general_service">General Service</option>
                  <option value="retail">Retail / Trading</option>
                </select>
              </Field>



                <Field label="TIN *">
                  <input
                    type="text"
                    value={formData.tin}
                    onChange={(e) => handleInputChange("tin", e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Tax Identification Number"
                    disabled={loading}
                  />
                </Field>

                <Field label="RDO Code *">
                  <input
                    type="text"
                    value={formData.rdoCode}
                    onChange={(e) => handleInputChange("rdoCode", e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="RDO Code"
                    disabled={loading}
                  />
                </Field>

                {isAccountingFirm && (
                  <BranchCodeField
                    value={formData.branchCode}
                    onChange={(v) => handleInputChange("branchCode", v)}
                    disabled={loading}
                  />
                )}
              </div>
            </section>
          )}

          {STEPS[stepIndex].key === "type" && (
            <section>
              <h2 className="text-xl font-semibold mb-1">
                {formData.clientType === "corporation" ? "Corporation Details" : "Individual Details"}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Only required fields will be enforced. Keep onboarding fast.
              </p>

              {formData.clientType === "corporation" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="SEC Registration Number *">
                    <input
                      type="text"
                      value={formData.corporationDetails.secRegistrationNumber}
                      onChange={(e) =>
                        handleInputChange("corporationDetails.secRegistrationNumber", e.target.value)
                      }
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="SEC Registration Number"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="Business Style">
                    <input
                      type="text"
                      value={formData.corporationDetails.businessStyle}
                      onChange={(e) => handleInputChange("corporationDetails.businessStyle", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Business Style"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="Date of Incorporation">
                    <input
                      type="date"
                      value={formData.corporationDetails.dateOfIncorporation}
                      onChange={(e) => handleInputChange("corporationDetails.dateOfIncorporation", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      disabled={loading}
                    />
                  </Field>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Birth Date">
                    <input
                      type="date"
                      value={formData.individualDetails.birthDate}
                      onChange={(e) => handleInputChange("individualDetails.birthDate", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="Birth Place">
                    <input
                      type="text"
                      value={formData.individualDetails.birthPlace}
                      onChange={(e) => handleInputChange("individualDetails.birthPlace", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Birth Place"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="Citizenship">
                    <input
                      type="text"
                      value={formData.individualDetails.citizenship}
                      onChange={(e) => handleInputChange("individualDetails.citizenship", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Citizenship"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="Occupation / Line of Business">
                    <input
                      type="text"
                      value={formData.individualDetails.occupation}
                      onChange={(e) => handleInputChange("individualDetails.occupation", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Occupation"
                      disabled={loading}
                    />
                  </Field>
                </div>
              )}
            </section>
          )}

          {STEPS[stepIndex].key === "contact" && (
            <section className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Email">
                    <input
                      type="email"
                      value={formData.contactInfo.email}
                      onChange={(e) => handleInputChange("contactInfo.email", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Email address"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="Phone">
                    <input
                      type="text"
                      value={formData.contactInfo.phone}
                      onChange={(e) => handleInputChange("contactInfo.phone", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Phone number"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="Mobile">
                    <input
                      type="text"
                      value={formData.contactInfo.mobile}
                      onChange={(e) => handleInputChange("contactInfo.mobile", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Mobile number"
                      disabled={loading}
                    />
                  </Field>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold mb-4">Address Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Unit / Floor">
                    <input
                      type="text"
                      value={formData.address.unitFloor}
                      onChange={(e) => handleInputChange("address.unitFloor", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Unit/Floor"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="Building">
                    <input
                      type="text"
                      value={formData.address.building}
                      onChange={(e) => handleInputChange("address.building", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Building"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="Street" className="md:col-span-2">
                    <input
                      type="text"
                      value={formData.address.street}
                      onChange={(e) => handleInputChange("address.street", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Street"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="Barangay">
                    <input
                      type="text"
                      value={formData.address.barangay}
                      onChange={(e) => handleInputChange("address.barangay", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Barangay"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="City / Municipality">
                    <input
                      type="text"
                      value={formData.address.cityMunicipality}
                      onChange={(e) => handleInputChange("address.cityMunicipality", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="City/Municipality"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="Province">
                    <input
                      type="text"
                      value={formData.address.province}
                      onChange={(e) => handleInputChange("address.province", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Province"
                      disabled={loading}
                    />
                  </Field>

                  <Field label="ZIP Code">
                    <input
                      type="text"
                      value={formData.address.zipCode}
                      onChange={(e) => handleInputChange("address.zipCode", e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="ZIP Code"
                      disabled={loading}
                    />
                  </Field>
                </div>
              </div>
            </section>
          )}

          {STEPS[stepIndex].key === "tax" && (
            <section>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Tax Obligations</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Select what applies to this client. These drive the Tax Modules in the sidebar.
                  </p>
                </div>

                <div className="text-xs text-gray-500">
                  Tip: You can update obligations later.
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {taxObligations.map((ob) => {
                  const checked = !!formData.taxObligations[ob.key];
                  return (
                    <button
                      key={ob.key}
                      type="button"
                      onClick={() => handleTaxObligationChange(ob.key, !checked)}
                      className={`text-left border rounded-lg p-4 transition-colors ${
                        checked ? "border-teal-300 bg-teal-50" : "border-gray-200 bg-white hover:bg-gray-50"
                      }`}
                      disabled={loading}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-gray-900">{ob.title}</div>
                          <div className="text-xs text-gray-500">{ob.subtitle}</div>
                          <div className="text-sm text-gray-600 mt-2">{ob.desc}</div>
                        </div>

                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => handleTaxObligationChange(ob.key, e.target.checked)}
                          className="h-4 w-4 mt-1 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                          onClick={(e) => e.stopPropagation()}
                          disabled={loading}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>

              {isAccountingFirm && (
                <WhtProfileFields
                  value={formData.whtProfile}
                  taxObligations={formData.taxObligations}
                  onChange={(whtProfile) => setFormData((prev) => ({ ...prev, whtProfile }))}
                  disabled={loading || isLocked}
                />
              )}
            </section>
          )}
        </div>
        </fieldset>

        {/* Actions */}
        <div className="flex items-center justify-between mt-6">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleBack}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading || stepIndex === 0}
            >
              Back
            </button>

            {stepIndex < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-teal-600 hover:bg-teal-700"
                disabled={loading}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-teal-600 hover:bg-teal-700"
                disabled={loading || saving || isLocked}
              >
                {saving ? "Saving..." : isEdit ? "Update Client" : "Create Client"}
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={handleCancel}
            className="text-sm text-gray-500 hover:text-gray-700"
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </Layout>
  );
};

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="block text-gray-700 text-sm font-bold mb-2">{label}</label>
    {children}
  </div>
);

export default NewAccountingClientPage;
